import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  Alert,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Stack
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Warning,
  VerifiedUser,
  Description,
  Image as ImageIcon,
  Download,
  ZoomIn,
  ThumbUp,
  ThumbDown,
  AccessTime,
  Refresh,
  DirectionsCar,
  Person,
  Edit,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const DRIVER_DOC_TEMPLATES = [
  { label: 'Select a template note...', value: '' },
  { label: 'Expired License', value: "Expired License: The uploaded driver's license has expired. Please upload a valid, unexpired license." },
  { label: 'Blurry Document', value: "Blurry Document: The image of your driver's license is blurry or illegible. Please take a clear photo showing all text and details clearly." },
  { label: 'Incorrect Document Type', value: "Incorrect Document Type: The uploaded document does not match the requested type. Please upload the correct driver's license photo." },
  { label: 'Name Mismatch', value: "Name Mismatch: The name on your driver's license does not match the name on your profile. Please contact support if this is a mistake." }
];

const VEHICLE_DOC_TEMPLATES = [
  { label: 'Select a template note...', value: '' },
  { label: 'Expired Insurance', value: "Expired Insurance: The insurance certificate has expired. Please upload a valid, active certificate of insurance." },
  { label: 'Plate Mismatch', value: "Plate Mismatch: The license plate number on the uploaded registration does not match the license plate in your vehicle details." },
  { label: 'Invalid Registration', value: "Incorrect Vehicle Registration: The uploaded document is not a valid vehicle registration certificate. Please upload the correct document." },
  { label: 'Blurry Vehicle Photos', value: "Unclear Photos: The uploaded vehicle photos are unclear or do not show the vehicle properly. Please upload clear photos of the front, back, and side of your vehicle." }
];

interface VehicleForVerification {
  id: string;
  year: number;
  color: string;
  licensePlate: string;
  seats: number;
  verificationStatus: string;
  adminNote?: string;
  reviewedAt?: string;
  make: { id: string; name: string };
  model: { id: string; name: string };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    phoneNumber: string;
  };
  documents?: Document[];
}

interface Driver {
  id: string;
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  status: string;
  phoneVerified: boolean;
  emailVerified: boolean;
  driverRating: number;
  totalTripsAsDriver: number;
  createdAt: string;
  profilePicture?: string;
  driverRejectionReason?: string;
  documents?: Document[];
  vehicles?: any[];
}

interface Document {
  id: string;
  userId: string;
  documentType?: string;
  type?: string;
  fileUrl: string;
  status: string;
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  expiryDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const VerificationPage: React.FC = () => {
  // Main tab: 0 = Drivers, 1 = Vehicles
  const [mainTab, setMainTab] = useState(0);

  // Driver verification state
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [reviewAction, setReviewAction] = useState<'APPROVED' | 'REJECTED' | 'RESUBMIT'>('APPROVED');
  const [reviewNotes, setReviewNotes] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info', message: string } | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [imageDialog, setImageDialog] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');

  // Vehicle verification state
  const [vehicles, setVehicles] = useState<VehicleForVerification[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclePage, setVehiclePage] = useState(0);
  const [vehicleRowsPerPage, setVehicleRowsPerPage] = useState(25);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleFilterStatus, setVehicleFilterStatus] = useState('PENDING,NEEDS_UPDATE,REJECTED');
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleForVerification | null>(null);
  const [vehicleReviewDialog, setVehicleReviewDialog] = useState(false);
  const [vehicleReviewAction, setVehicleReviewAction] = useState<'approve' | 'reject' | 'request-update'>('approve');
  const [vehicleReviewNotes, setVehicleReviewNotes] = useState('');
  const [vehicleDocIndex, setVehicleDocIndex] = useState(0);

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchDriversForVerification();
  }, [page, rowsPerPage, search, filterStatus]);

  useEffect(() => {
    if (mainTab === 1) {
      fetchVehiclesForVerification();
    }
  }, [mainTab, vehiclePage, vehicleRowsPerPage, vehicleSearch, vehicleFilterStatus]);

  const fetchVehiclesForVerification = async () => {
    setVehiclesLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (vehiclePage + 1).toString(),
        limit: vehicleRowsPerPage.toString(),
        status: vehicleFilterStatus,
        ...(vehicleSearch && { search: vehicleSearch })
      });

      const response = await axios.get(`${apiUrl}/admin/vehicle-verification/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setVehicles(response.data.data.vehicles);
      setTotalVehicles(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch vehicles for verification:', error);
      showAlert('error', 'Failed to load vehicles');
    } finally {
      setVehiclesLoading(false);
    }
  };

  const fetchVehicleDetails = async (vehicleId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/vehicle-verification/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedVehicle(response.data.data.vehicle);
      setVehicleDocIndex(0);
    } catch (error) {
      console.error('Failed to fetch vehicle details:', error);
      showAlert('error', 'Failed to load vehicle details');
    }
  };

  const handleVehicleReview = async () => {
    if (!selectedVehicle) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/vehicle-verification/${selectedVehicle.id}/${vehicleReviewAction}`,
        { reviewNotes: vehicleReviewNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const actionLabel = vehicleReviewAction === 'approve' ? 'approved' :
                          vehicleReviewAction === 'reject' ? 'rejected' : 'update requested';
      showAlert('success', `Vehicle ${actionLabel} successfully`);
      setVehicleReviewDialog(false);
      setVehicleReviewNotes('');
      setSelectedVehicle(null);
      fetchVehiclesForVerification();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to process vehicle review');
    }
  };

  const getVehicleStatusChip = (status: string) => {
    const config: Record<string, { color: 'warning' | 'success' | 'error' | 'info' | 'default'; icon: React.ReactElement }> = {
      PENDING: { color: 'warning', icon: <AccessTime /> },
      VERIFIED: { color: 'success', icon: <CheckCircle /> },
      REJECTED: { color: 'error', icon: <Cancel /> },
      NEEDS_UPDATE: { color: 'info', icon: <Edit /> }
    };
    const c = config[status] || { color: 'default', icon: <Warning /> };
    return <Chip icon={c.icon} label={status.replace('_', ' ')} size="small" color={c.color} />;
  };

  const fetchDriversForVerification = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        role: 'DRIVER',
        ...(search && { search })
      });

      const response = await axios.get(`${apiUrl}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter drivers that have documents needing review
      let filteredDrivers = response.data.users.filter((driver: Driver) => {
        if (!driver.documents || driver.documents.length === 0) return filterStatus === 'NO_DOCUMENTS';

        const hasPending = driver.documents.some((doc: Document) => doc.status === 'PENDING');
        const allApproved = driver.documents.every((doc: Document) => doc.status === 'APPROVED');
        const anyRejected = driver.documents.some((doc: Document) => doc.status === 'REJECTED');

        if (filterStatus === 'PENDING') return hasPending;
        if (filterStatus === 'APPROVED') return allApproved;
        if (filterStatus === 'REJECTED') return anyRejected;
        if (filterStatus === 'ALL') return true;

        return false;
      });

      setDrivers(filteredDrivers);
      setTotalDrivers(filteredDrivers.length);
    } catch (error) {
      console.error('Failed to fetch drivers for verification:', error);
      showAlert('error', 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewDocument = async () => {
    if (!selectedDocument || !selectedDriver) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/documents/${selectedDocument.id}/review`,
        {
          status: reviewAction,
          reviewNotes: reviewNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', `Document ${reviewAction.toLowerCase()} successfully`);
      setReviewDialog(false);
      setReviewNotes('');
      fetchDriversForVerification();

      // Refresh selected driver details
      if (selectedDriver) {
        const updatedDriver = drivers.find(d => d.id === selectedDriver.id);
        if (updatedDriver) setSelectedDriver(updatedDriver);
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to review document');
    }
  };

  const handleBulkApprove = async (driverId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const driver = drivers.find(d => d.id === driverId);

      if (!driver || !driver.documents) return;

      // Approve all pending documents
      const pendingDocs = driver.documents.filter(doc => doc.status === 'PENDING');

      if (pendingDocs.length === 0) {
        showAlert('warning', 'No pending documents to approve');
        return;
      }

      await Promise.all(
        pendingDocs.map(doc =>
          axios.patch(
            `${apiUrl}/admin/documents/${doc.id}/review`,
            { status: 'APPROVED', reviewNotes: 'Bulk approved by admin' },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      // Update driver status to ACTIVE
      await axios.patch(
        `${apiUrl}/admin/users/${driverId}/status`,
        { status: 'ACTIVE' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Driver verified and approved successfully');
      fetchDriversForVerification();
      setSelectedDriver(null);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to approve driver');
    }
  };

  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'DRIVERS_LICENSE_FRONT': "Driver's License (Front)",
      'DRIVERS_LICENSE_BACK': "Driver's License (Back)",
      'GOVERNMENT_ID': 'Government ID',
      'VEHICLE_REGISTRATION': 'Vehicle Registration',
      'INSURANCE_CERTIFICATE': 'Insurance Certificate',
      'VEHICLE_PHOTO_FRONT': 'Vehicle Photo (Front)',
      'VEHICLE_PHOTO_BACK': 'Vehicle Photo (Back)',
      'VEHICLE_PHOTO_SIDE': 'Vehicle Photo (Side)',
      'VEHICLE_PHOTO_INTERIOR': 'Vehicle Photo (Interior)',
      'SELFIE_WITH_ID': 'Selfie with ID'
    };
    return labels[type] || type.replace(/_/g, ' ');
  };

  const getDocType = (doc: Document) => (doc.type || doc.documentType || '').toString();

  const getVerificationStatus = (driver: Driver) => {
    if (!driver.documents || driver.documents.length === 0) {
      return { status: 'No Documents', color: 'error', icon: <Warning /> };
    }

    const allApproved = driver.documents.every(doc => doc.status === 'APPROVED');
    const anyPending = driver.documents.some(doc => doc.status === 'PENDING');
    const anyRejected = driver.documents.some(doc => doc.status === 'REJECTED');

    if (allApproved) {
      return { status: 'Verified', color: 'success', icon: <CheckCircle /> };
    } else if (anyRejected) {
      return { status: 'Rejected', color: 'error', icon: <Cancel /> };
    } else if (anyPending) {
      return { status: 'Pending Review', color: 'warning', icon: <AccessTime /> };
    }
    return { status: 'Incomplete', color: 'default', icon: <Warning /> };
  };

  const getDocumentStats = (driver: Driver) => {
    if (!driver.documents || driver.documents.length === 0) {
      return { total: 0, approved: 0, pending: 0, rejected: 0 };
    }

    return {
      total: driver.documents.length,
      approved: driver.documents.filter(d => d.status === 'APPROVED').length,
      pending: driver.documents.filter(d => d.status === 'PENDING').length,
      rejected: driver.documents.filter(d => d.status === 'REJECTED').length
    };
  };

  const openImageViewer = (url: string) => {
    setSelectedImageUrl(url);
    setImageDialog(true);
  };

  const handleViewDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setTabValue(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Verification Center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and verify driver documents and vehicles
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={mainTab === 0 ? fetchDriversForVerification : fetchVehiclesForVerification}
        >
          Refresh
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Main Tabs: Drivers / Vehicles */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}>
          <Tab icon={<Person />} label="Drivers" iconPosition="start" />
          <Tab icon={<DirectionsCar />} label="Vehicles" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* ==================== DRIVERS TAB ==================== */}
      {mainTab === 0 && (
        <>

      {/* Stats Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <AccessTime color="warning" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {drivers.filter(d => getVerificationStatus(d).status === 'Pending Review').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">Pending Review</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {drivers.filter(d => getVerificationStatus(d).status === 'Verified').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">Verified</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Cancel color="error" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {drivers.filter(d => getVerificationStatus(d).status === 'Rejected').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">Rejected</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Warning color="action" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {drivers.filter(d => !d.documents || d.documents.length === 0).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">No Documents</Typography>
        </Paper>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Verification Status</InputLabel>
            <Select
              value={filterStatus}
              label="Verification Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="PENDING">Pending Review</MenuItem>
              <MenuItem value="APPROVED">Verified</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="NO_DOCUMENTS">No Documents</MenuItem>
              <MenuItem value="ALL">All Drivers</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => {
            setSearch('');
            setFilterStatus('PENDING');
          }}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Drivers List */}
        <Box sx={{ flex: selectedDriver ? '0 0 40%' : '1' }}>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : drivers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No drivers found for verification
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    drivers.map((driver) => {
                      const verification = getVerificationStatus(driver);
                      const stats = getDocumentStats(driver);
                      const isSelected = selectedDriver?.id === driver.id;

                      return (
                        <TableRow
                          key={driver.id}
                          hover
                          selected={isSelected}
                          sx={{ cursor: 'pointer', bgcolor: isSelected ? 'action.selected' : 'inherit' }}
                          onClick={() => handleViewDriver(driver)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar src={driver.profilePicture}>
                                {driver.firstName[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {driver.preferredName || driver.firstName} {driver.lastName ? driver.lastName.charAt(0) + '.' : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {driver.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <Chip label={`${stats.approved}/${stats.total}`} size="small" color="success" />
                              {stats.pending > 0 && (
                                <Chip label={`${stats.pending} pending`} size="small" color="warning" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={verification.icon}
                              label={verification.status}
                              size="small"
                              color={verification.color as any}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={(e) => {
                              e.stopPropagation();
                              handleViewDriver(driver);
                            }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalDrivers}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </Box>

        {/* Driver Details Panel */}
        {selectedDriver && (
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3 }}>
              {/* Driver Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar src={selectedDriver.profilePicture} sx={{ width: 60, height: 60 }}>
                  {selectedDriver.firstName[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {selectedDriver.preferredName || selectedDriver.firstName} {selectedDriver.lastName ? selectedDriver.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDriver.email} • {selectedDriver.phoneNumber}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {(() => {
                      const verification = getVerificationStatus(selectedDriver);
                      return (
                        <Chip
                          icon={verification.icon}
                          label={verification.status}
                          size="small"
                          color={verification.color as any}
                        />
                      );
                    })()}
                  </Box>
                  {selectedDriver.driverRejectionReason && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="caption" fontWeight="bold" display="block">AI Rejection Reason:</Typography>
                      <Typography variant="caption">{selectedDriver.driverRejectionReason}</Typography>
                    </Alert>
                  )}
                </Box>
                {selectedDriver.documents && selectedDriver.documents.some(d => d.status === 'PENDING') && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleBulkApprove(selectedDriver.id)}
                  >
                    Approve All
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Tabs */}
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Documents" />
                <Tab label="Vehicles" />
                <Tab label="Profile Info" />
              </Tabs>

              {/* Documents Tab */}
              <TabPanel value={tabValue} index={0}>
                {selectedDriver.documents && selectedDriver.documents.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {selectedDriver.documents.map((doc) => (
                      <Box key={doc.id}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="200"
                            image={doc.fileUrl}
                            alt={getDocumentTypeLabel(getDocType(doc))}
                            sx={{ objectFit: 'contain', bgcolor: 'grey.100', cursor: 'pointer' }}
                            onClick={() => openImageViewer(doc.fileUrl)}
                          />
                          <CardContent>
                            <Typography variant="body2" fontWeight="medium" gutterBottom>
                              {getDocumentTypeLabel(getDocType(doc))}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                            </Typography>
                            {doc.expiryDate && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Expires: {new Date(doc.expiryDate).toLocaleDateString()}
                              </Typography>
                            )}
                            <Chip
                              label={doc.status}
                              size="small"
                              color={
                                doc.status === 'APPROVED' ? 'success' :
                                doc.status === 'REJECTED' ? 'error' :
                                doc.status === 'PENDING' ? 'warning' : 'default'
                              }
                              sx={{ mt: 1 }}
                            />
                            {doc.reviewNotes && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="caption">{doc.reviewNotes}</Typography>
                              </Alert>
                            )}
                          </CardContent>
                          <CardActions>
                            <Button
                              size="small"
                              startIcon={<ZoomIn />}
                              onClick={() => openImageViewer(doc.fileUrl)}
                            >
                              View
                            </Button>
                            {doc.status === 'PENDING' && (
                              <>
                                <Button
                                  size="small"
                                  color="success"
                                  startIcon={<ThumbUp />}
                                  onClick={() => {
                                    setSelectedDocument(doc);
                                    setReviewAction('APPROVED');
                                    setReviewDialog(true);
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<ThumbDown />}
                                  onClick={() => {
                                    setSelectedDocument(doc);
                                    setReviewAction('REJECTED');
                                    setReviewDialog(true);
                                  }}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </CardActions>
                        </Card>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="warning">No documents uploaded yet</Alert>
                )}
              </TabPanel>

              {/* Vehicles Tab */}
              <TabPanel value={tabValue} index={1}>
                {selectedDriver.vehicles && selectedDriver.vehicles.length > 0 ? (
                  <Stack spacing={2}>
                    {selectedDriver.vehicles.map((vehicle: any) => (
                      <Paper key={vehicle.id} sx={{ p: 2 }} elevation={2}>
                        <Typography variant="body1" fontWeight="medium">
                          {vehicle.make} {vehicle.model} ({vehicle.year})
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Color: {vehicle.color}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          License Plate: {vehicle.licensePlate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Seats: {vehicle.seats}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No vehicles registered</Alert>
                )}
              </TabPanel>

              {/* Profile Info Tab */}
              <TabPanel value={tabValue} index={2}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedDriver.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{selectedDriver.phoneNumber}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Registration Date</Typography>
                    <Typography variant="body1">{new Date(selectedDriver.createdAt).toLocaleDateString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Account Status</Typography>
                    <Typography variant="body1">{selectedDriver.status}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email Verified</Typography>
                    <Typography variant="body1">{selectedDriver.emailVerified ? 'Yes' : 'No'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone Verified</Typography>
                    <Typography variant="body1">{selectedDriver.phoneVerified ? 'Yes' : 'No'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Trips</Typography>
                    <Typography variant="body1">{selectedDriver.totalTripsAsDriver}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Driver Rating</Typography>
                    <Typography variant="body1">{selectedDriver.driverRating?.toFixed(1) || 'N/A'}</Typography>
                  </Box>
                </Stack>
              </TabPanel>
            </Paper>
          </Box>
        )}
      </Box>
      </>
      )}

      {/* ==================== VEHICLES TAB ==================== */}
      {mainTab === 1 && (
        <>
          {/* Vehicle Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by license plate or driver name..."
                value={vehicleSearch}
                onChange={(e) => setVehicleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={vehicleFilterStatus}
                  label="Status"
                  onChange={(e) => setVehicleFilterStatus(e.target.value)}
                >
                  <MenuItem value="PENDING,NEEDS_UPDATE,REJECTED">Needs Review</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="NEEDS_UPDATE">Needs Update</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="VERIFIED">Verified</MenuItem>
                  <MenuItem value="PENDING,VERIFIED,REJECTED,NEEDS_UPDATE">All</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={() => {
                setVehicleSearch('');
                setVehicleFilterStatus('PENDING,NEEDS_UPDATE,REJECTED');
              }}>
                Clear Filters
              </Button>
            </Box>
          </Paper>

          {/* Vehicle List */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>License Plate</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehiclesLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : vehicles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No vehicles found for verification
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    vehicles.map((vehicle) => (
                      <TableRow key={vehicle.id} hover sx={{ cursor: 'pointer' }} onClick={() => fetchVehicleDetails(vehicle.id)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DirectionsCar color="action" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {vehicle.year} {vehicle.make?.name} {vehicle.model?.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {vehicle.color} • {vehicle.seats} seats
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{vehicle.licensePlate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {vehicle.user?.preferredName || vehicle.user?.firstName} {vehicle.user?.lastName ? vehicle.user?.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vehicle.user?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{getVehicleStatusChip(vehicle.verificationStatus)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); fetchVehicleDetails(vehicle.id); }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalVehicles}
              page={vehiclePage}
              onPageChange={(e, newPage) => setVehiclePage(newPage)}
              rowsPerPage={vehicleRowsPerPage}
              onRowsPerPageChange={(e) => {
                setVehicleRowsPerPage(parseInt(e.target.value, 10));
                setVehiclePage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </>
      )}

      {/* Vehicle Review Dialog */}
      <Dialog open={!!selectedVehicle} onClose={() => setSelectedVehicle(null)} maxWidth="lg" fullWidth>
        {selectedVehicle && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Vehicle Review: {selectedVehicle.year} {selectedVehicle.make?.name} {selectedVehicle.model?.name}
                </Typography>
                {getVehicleStatusChip(selectedVehicle.verificationStatus)}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Left: Vehicle & Driver Info */}
                <Box sx={{ flex: '0 0 320px' }}>
                  <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Vehicle Info</Typography>
                    <Stack spacing={1}>
                      <Box><Typography variant="caption" color="text.secondary">Make/Model</Typography><Typography variant="body2">{selectedVehicle.make?.name} {selectedVehicle.model?.name}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Year</Typography><Typography variant="body2">{selectedVehicle.year}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Color</Typography><Typography variant="body2">{selectedVehicle.color}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">License Plate</Typography><Typography variant="body2" fontWeight="bold">{selectedVehicle.licensePlate}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Seats</Typography><Typography variant="body2">{selectedVehicle.seats}</Typography></Box>
                    </Stack>
                  </Paper>
                  <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Driver Info</Typography>
                    <Stack spacing={1}>
                      <Box><Typography variant="caption" color="text.secondary">Name</Typography><Typography variant="body2">{selectedVehicle.user?.preferredName || selectedVehicle.user?.firstName} {selectedVehicle.user?.lastName ? selectedVehicle.user?.lastName.charAt(0) + '.' : ''}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2">{selectedVehicle.user?.email}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2">{selectedVehicle.user?.phoneNumber}</Typography></Box>
                    </Stack>
                  </Paper>
                  {selectedVehicle.adminNote && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight="bold">Previous Admin Note:</Typography>
                      <Typography variant="body2">{selectedVehicle.adminNote}</Typography>
                    </Alert>
                  )}
                  {/* Review Actions */}
                  <Stack spacing={1}>
                    <Button fullWidth variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => { setVehicleReviewAction('approve'); setVehicleReviewDialog(true); }}>
                      Approve Vehicle
                    </Button>
                    <Button fullWidth variant="outlined" color="warning" startIcon={<Edit />} onClick={() => { setVehicleReviewAction('request-update'); setVehicleReviewDialog(true); }}>
                      Request Update
                    </Button>
                    <Button fullWidth variant="outlined" color="error" startIcon={<Cancel />} onClick={() => { setVehicleReviewAction('reject'); setVehicleReviewDialog(true); }}>
                      Reject Vehicle
                    </Button>
                  </Stack>
                </Box>

                {/* Right: Document Viewer */}
                <Box sx={{ flex: 1, minHeight: 400 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Documents ({selectedVehicle.documents?.length || 0})
                  </Typography>
                  {selectedVehicle.documents && selectedVehicle.documents.length > 0 ? (
                    <Box>
                      {/* Document Navigation */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <IconButton disabled={vehicleDocIndex === 0} onClick={() => setVehicleDocIndex(i => i - 1)}>
                          <ChevronLeft />
                        </IconButton>
                        <Typography variant="body2">
                          {getDocumentTypeLabel(selectedVehicle.documents[vehicleDocIndex]?.type || '')} ({vehicleDocIndex + 1}/{selectedVehicle.documents.length})
                        </Typography>
                        <IconButton disabled={vehicleDocIndex >= selectedVehicle.documents.length - 1} onClick={() => setVehicleDocIndex(i => i + 1)}>
                          <ChevronRight />
                        </IconButton>
                      </Box>
                      {/* Document Image */}
                      <Box sx={{ position: 'relative', bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box
                          component="img"
                          src={selectedVehicle.documents[vehicleDocIndex]?.fileUrl}
                          alt="Document"
                          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                          onClick={() => openImageViewer(selectedVehicle.documents![vehicleDocIndex]?.fileUrl)}
                        />
                      </Box>
                      {/* Document Status */}
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={selectedVehicle.documents[vehicleDocIndex]?.status}
                          size="small"
                          color={
                            selectedVehicle.documents[vehicleDocIndex]?.status === 'APPROVED' ? 'success' :
                            selectedVehicle.documents[vehicleDocIndex]?.status === 'REJECTED' ? 'error' :
                            selectedVehicle.documents[vehicleDocIndex]?.status === 'RESUBMISSION_REQUIRED' ? 'warning' : 'default'
                          }
                        />
                        <Button size="small" startIcon={<ZoomIn />} onClick={() => openImageViewer(selectedVehicle.documents![vehicleDocIndex]?.fileUrl)}>
                          Full View
                        </Button>
                        <Button size="small" startIcon={<Download />} onClick={() => window.open(selectedVehicle.documents![vehicleDocIndex]?.fileUrl, '_blank')}>
                          Download
                        </Button>
                      </Box>
                      {selectedVehicle.documents[vehicleDocIndex]?.reviewNotes && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="caption" display="block" fontWeight="bold">AI / Review Notes:</Typography>
                          <Typography variant="caption">{selectedVehicle.documents[vehicleDocIndex]?.reviewNotes}</Typography>
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <Alert severity="warning">No documents uploaded for this vehicle</Alert>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedVehicle(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Vehicle Review Action Dialog */}
      <Dialog open={vehicleReviewDialog} onClose={() => setVehicleReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {vehicleReviewAction === 'approve' ? 'Approve Vehicle' :
           vehicleReviewAction === 'reject' ? 'Reject Vehicle' : 'Request Document Update'}
        </DialogTitle>
        <DialogContent>
          {vehicleReviewAction !== 'approve' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Prepared Notes Template</InputLabel>
              <Select
                label="Prepared Notes Template"
                onChange={(e) => setVehicleReviewNotes(e.target.value as string)}
                defaultValue=""
              >
                {VEHICLE_DOC_TEMPLATES.map((tmpl, idx) => (
                  <MenuItem key={idx} value={tmpl.value}>{tmpl.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            label={vehicleReviewAction === 'approve' ? 'Notes (optional)' : 'Reason (required)'}
            multiline
            rows={4}
            value={vehicleReviewNotes}
            onChange={(e) => setVehicleReviewNotes(e.target.value)}
            placeholder={
              vehicleReviewAction === 'approve'
                ? 'Add any notes...'
                : 'Explain what needs to be fixed or why the vehicle is rejected...'
            }
            sx={{ mt: 2 }}
            required={vehicleReviewAction !== 'approve'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleReviewDialog(false)}>Cancel</Button>
          <Button
            onClick={handleVehicleReview}
            variant="contained"
            color={vehicleReviewAction === 'approve' ? 'success' : vehicleReviewAction === 'reject' ? 'error' : 'warning'}
            disabled={vehicleReviewAction !== 'approve' && !vehicleReviewNotes.trim()}
          >
            {vehicleReviewAction === 'approve' ? 'Approve' :
             vehicleReviewAction === 'reject' ? 'Reject' : 'Request Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Document Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'APPROVED' ? 'Approve Document' : 'Reject Document'}
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box sx={{ mb: 2, pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Document Type
              </Typography>
              <Typography variant="body1" fontWeight="medium" gutterBottom>
                {getDocumentTypeLabel(getDocType(selectedDocument))}
              </Typography>
            </Box>
          )}
          {reviewAction === 'REJECTED' && (
            <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
              <InputLabel>Prepared Notes Template</InputLabel>
              <Select
                label="Prepared Notes Template"
                onChange={(e) => setReviewNotes(e.target.value as string)}
                defaultValue=""
              >
                {DRIVER_DOC_TEMPLATES.map((tmpl, idx) => (
                  <MenuItem key={idx} value={tmpl.value}>{tmpl.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            label={reviewAction === 'APPROVED' ? 'Review Notes (optional)' : 'Reason for Rejection (required)'}
            multiline
            rows={4}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder={
              reviewAction === 'APPROVED'
                ? 'Add any notes about this document...'
                : 'Please provide a reason for rejection so the driver can resubmit...'
            }
            required={reviewAction === 'REJECTED'}
            sx={{ mt: reviewAction === 'REJECTED' ? 2 : 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReviewDocument}
            variant="contained"
            color={reviewAction === 'APPROVED' ? 'success' : 'error'}
            disabled={reviewAction === 'REJECTED' && !reviewNotes.trim()}
          >
            {reviewAction === 'APPROVED' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageDialog}
        onClose={() => setImageDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          <Box
            component="img"
            src={selectedImageUrl}
            alt="Document"
            sx={{ width: '100%', height: 'auto', maxHeight: '90vh', objectFit: 'contain' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(selectedImageUrl, '_blank')} startIcon={<Download />}>
            Download
          </Button>
          <Button onClick={() => setImageDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationPage;
